import type { ImportProblem, ImportSettings, RouteChoice } from '@didto/domain';
import {
  categoriesOf,
  fractionDigitsFor,
  modifierSheetTemplate,
  newCommandId,
  parseCsv,
  planMenuImport,
  readLoyverseItems,
  readModifierSheet,
} from '@didto/domain';
import {
  Button,
  Checkbox,
  ConfirmSheet,
  ErrorState,
  Field,
  Loading,
  MetricCard,
  Notice,
  PageHeader,
  Section,
  Select,
} from '@didto/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { NoAccess } from '@/app/guards';
import { useSession } from '@/app/session';
import { downloadText } from '@/lib/download';
import { errorText } from '@/lib/errors';
import { fetchImportSnapshot, importKeys, runImportStep } from '@/lib/menuImport';
import { useOnline } from '@/lib/network';
import './catalog.css';

type Loaded = { name: string; text: string } | null;

const ROUTE_LABEL: Record<RouteChoice, string> = {
  KITCHEN: 'Kitchen',
  BAR: 'Bar',
  NONE: 'No preparation',
};

async function readFile(e: React.ChangeEvent<HTMLInputElement>): Promise<Loaded> {
  const f = e.target.files?.[0];
  return f ? { name: f.name, text: await f.text() } : null;
}

/**
 * Import the menu from a Loyverse items export (ADR-044). The check runs in the browser and writes
 * nothing; the import sends ordinary catalog commands one by one under the owner's name, then
 * reads the catalog again and checks that nothing is left to change.
 */
export function MenuImportPage() {
  const s = useSession();
  if (!s.canActOnOwners)
    return (
      <NoAccess reason="Importing the menu is the owner's, from the owner's signed-in account." />
    );
  return <MenuImport />;
}

function MenuImport() {
  const s = useSession();
  const qc = useQueryClient();
  const online = useOnline();
  const businessId = s.me?.business?.id ?? '';
  const businessName = s.me?.business?.name ?? 'the business';
  const fd = fractionDigitsFor(s.me?.business?.currency ?? 'PHP');
  const [items, setItems] = useState<Loaded>(null);
  const [mods, setMods] = useState<Loaded>(null);
  const [store, setStore] = useState<string | null>(null);
  const [routes, setRoutes] = useState<Record<string, RouteChoice | undefined>>({});
  const [importCosts, setImportCosts] = useState(true);
  const [skipUnsupported, setSkipUnsupported] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /** The step ids the owner confirmed; the import sends only that plan. */
  const [confirmed, setConfirmed] = useState<string[] | null>(null);
  /** The file or the catalog changed between the confirmation and the import: nothing was sent. */
  const [changed, setChanged] = useState(false);
  const [run, setRun] = useState<{
    total: number;
    done: number;
    failed: { label: string; message: string } | null;
    finished: boolean;
  } | null>(null);

  const snapshot = useQuery({ queryKey: importKeys.snapshot, queryFn: fetchImportSnapshot });
  const file = useMemo(
    () => (items ? readLoyverseItems(parseCsv(items.text), { store, fractionDigits: fd }) : null),
    [items, store, fd],
  );
  const sheet = useMemo(
    () => (mods ? readModifierSheet(parseCsv(mods.text), { fractionDigits: fd }) : null),
    [mods, fd],
  );
  const categories = useMemo(() => (file ? categoriesOf(file) : []), [file]);
  const settings: ImportSettings = {
    businessId,
    routeByCategory: routes,
    importCosts,
    skipUnsupported,
  };
  const plan = useMemo(
    () => (file && snapshot.data ? planMenuImport(file, sheet, snapshot.data, settings) : null),
    // settings is rebuilt each render from these values
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [file, sheet, snapshot.data, businessId, routes, importCosts, skipUnsupported],
  );
  const running = run !== null && !run.finished;

  const apply = async () => {
    setConfirming(false);
    if (!file || !plan || plan.blocked || !confirmed) return;
    // Read the catalog again and plan again: what is sent is exactly the plan that was confirmed,
    // checked against the catalog as it is now (someone may have changed it meanwhile).
    let steps;
    try {
      const fresh = await qc.fetchQuery({
        queryKey: importKeys.snapshot,
        queryFn: fetchImportSnapshot,
        staleTime: 0,
      });
      const now = planMenuImport(file, sheet, fresh, settings);
      const same =
        !now.blocked &&
        now.steps.length === confirmed.length &&
        now.steps.every((st, i) => st.stepId === confirmed[i]);
      if (!same) {
        setChanged(true);
        return;
      }
      steps = now.steps;
    } catch (e) {
      setRun({
        total: 0,
        done: 0,
        failed: { label: 'Reading the catalog', message: errorText(e) },
        finished: true,
      });
      return;
    }
    const runId = newCommandId();
    setRun({ total: steps.length, done: 0, failed: null, finished: false });
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]!;
      try {
        await runImportStep(step, runId);
        setRun({ total: steps.length, done: i + 1, failed: null, finished: false });
      } catch (e) {
        setRun({
          total: steps.length,
          done: i,
          failed: { label: step.label, message: errorText(e) },
          finished: true,
        });
        await qc.invalidateQueries({ queryKey: ['catalog'] });
        return;
      }
    }
    setRun({ total: steps.length, done: steps.length, failed: null, finished: true });
    await qc.invalidateQueries({ queryKey: ['catalog'] });
  };

  const byLevel = (l: ImportProblem['level']) => plan?.problems.filter((p) => p.level === l) ?? [];
  const errors = byLevel('error');
  const warnings = byLevel('warning');
  const infos = byLevel('info');
  const nothingToDo = plan !== null && !plan.blocked && plan.steps.length === 0;

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Catalog"
        title="Import the menu"
        subtitle="From a Loyverse items export. The check below writes nothing; only Import does."
        actions={
          <Link to="/catalog" className="d-btn d-btn--ghost">
            Back to products
          </Link>
        }
      />

      {snapshot.isError && (
        <ErrorState
          title="Could not read the catalog"
          message={errorText(snapshot.error)}
          onRetry={() => void snapshot.refetch()}
        />
      )}

      <Section
        title="1. Files"
        description="In Loyverse Back Office: Items, Export. Loyverse does not export modifier options, so list them in the modifier sheet."
      >
        <div className="catalog__pair">
          <Field
            label="Loyverse items export (CSV)"
            help={items ? `${items.name}: ${file?.items.length ?? 0} items read` : undefined}
          >
            {(id) => (
              <input
                id={id}
                type="file"
                accept=".csv,text/csv"
                className="d-input"
                data-testid="import-items-file"
                disabled={running}
                onChange={(e) => {
                  void readFile(e).then((f) => {
                    setItems(f);
                    setRun(null);
                  });
                }}
              />
            )}
          </Field>
          <Field
            label="Modifier sheet (CSV, if items use modifiers)"
            help={
              mods
                ? `${mods.name}: ${sheet?.groups.length ?? 0} groups read`
                : 'Modifier,Option,Price,Min,Max'
            }
          >
            {(id) => (
              <input
                id={id}
                type="file"
                accept=".csv,text/csv"
                className="d-input"
                data-testid="import-modifiers-file"
                disabled={running}
                onChange={(e) => {
                  void readFile(e).then((f) => {
                    setMods(f);
                    setRun(null);
                  });
                }}
              />
            )}
          </Field>
        </div>
        <div className="d-inline">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => downloadText('modifier-sheet-template.csv', modifierSheetTemplate())}
            data-testid="import-template"
          >
            Download the modifier sheet template
          </Button>
        </div>
        {file && file.stores.length > 1 && (
          <Field label="Store whose prices to import">
            {(id) => (
              <Select
                id={id}
                value={store ?? ''}
                onChange={(e) => setStore(e.target.value || null)}
                data-testid="import-store"
              >
                <option value="">Choose a store…</option>
                {file.stores.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </Section>

      {file && categories.length > 0 && (
        <Section
          title="2. Where each category is prepared"
          description="Loyverse does not export kitchen printer groups. Every item of a category goes to the same station."
        >
          <div className="d-table-scroll">
            <table className="d-table" data-testid="import-routes">
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col" className="d-num">
                    Items
                  </th>
                  <th scope="col">Prepared at</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.key}>
                    <th scope="row">{c.name || 'No category'}</th>
                    <td className="d-num">{c.items}</td>
                    <td>
                      <Select
                        aria-label={`Where ${c.name || 'items without a category'} are prepared`}
                        value={routes[c.key] ?? ''}
                        disabled={running}
                        onChange={(e) =>
                          setRoutes((r) => ({
                            ...r,
                            [c.key]: (e.target.value || undefined) as RouteChoice | undefined,
                          }))
                        }
                        data-testid={`import-route-${c.key || 'none'}`}
                      >
                        <option value="">Choose…</option>
                        {(Object.keys(ROUTE_LABEL) as RouteChoice[]).map((k) => (
                          <option key={k} value={k}>
                            {ROUTE_LABEL[k]}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Checkbox
            checked={importCosts}
            onChange={setImportCosts}
            disabled={running}
            testId="import-costs"
            label="Import each variant's cost as today's cost (one figure, no history)"
          />
          <Checkbox
            checked={skipUnsupported}
            onChange={setSkipUnsupported}
            disabled={running}
            testId="import-skip-unsupported"
            label="Leave out items sold by weight or with a variable price (didto. OS sells by unit at a fixed price)"
          />
        </Section>
      )}

      {file && !snapshot.data && !snapshot.isError && <Loading label="Reading the catalog…" />}

      {plan && (
        <Section title="3. Check" description="What an import would do. Nothing has been written.">
          <div className="d-metrics">
            <MetricCard
              label="Items to import"
              value={plan.counts.importedItems}
              testId="import-items"
            />
            <MetricCard label="Changes" value={plan.steps.length} testId="import-changes" />
            <MetricCard label="Already up to date" value={plan.counts.unchangedItems} />
            <MetricCard label="Left out" value={plan.counts.skippedItems} />
          </div>
          {errors.length > 0 && (
            <Notice
              tone="danger"
              title={`${errors.length} problem(s) stop the import`}
              testId="import-errors"
            >
              <ul className="import__list">
                {errors.map((p, i) => (
                  <li key={i}>
                    <span className="d-strong">{p.where}:</span> {p.message}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          {warnings.length > 0 && (
            <Notice tone="warning" title="Left out or changed" testId="import-warnings">
              <ul className="import__list">
                {warnings.map((p, i) => (
                  <li key={i}>
                    <span className="d-strong">{p.where}:</span> {p.message}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          {infos.length > 0 && (
            <Notice title="Good to know" testId="import-info">
              <ul className="import__list">
                {infos.map((p, i) => (
                  <li key={i}>
                    <span className="d-strong">{p.where}:</span> {p.message}
                  </li>
                ))}
              </ul>
            </Notice>
          )}
          {nothingToDo && (
            <Notice tone="success" title="Nothing to change" testId="import-nothing">
              The catalog already matches the file.
            </Notice>
          )}
          {plan.steps.length > 0 && (
            <details className="import__plan" data-testid="import-plan">
              <summary>The {plan.steps.length} changes, in order</summary>
              <ol className="import__list">
                {plan.steps.map((st) => (
                  <li key={st.stepId}>
                    <span className="d-muted">{st.action === 'assign' ? 'link' : st.action}</span>{' '}
                    {st.entity}: {st.label}
                  </li>
                ))}
              </ol>
            </details>
          )}
        </Section>
      )}

      {plan && (plan.steps.length > 0 || run) && (
        <Section title="4. Import">
          {run && !run.finished && (
            <Notice
              role="status"
              title={`Importing… ${run.done} of ${run.total}`}
              testId="import-progress"
            >
              Keep this page open until it finishes.
            </Notice>
          )}
          {run?.failed && (
            <Notice tone="danger" role="alert" title="The import stopped" testId="import-failed">
              {run.done} of {run.total} changes were made. It stopped at “{run.failed.label}”:{' '}
              {run.failed.message} Nothing after it was sent. Fix the cause, then check and import
              again: what is done stays done.
            </Notice>
          )}
          {changed && (
            <Notice tone="warning" role="alert" title="Not imported" testId="import-changed">
              The catalog changed after the check (someone edited it, or the check found something
              new). Nothing was sent. Read the check above again, then import.
            </Notice>
          )}
          {run?.finished && !run.failed && nothingToDo && (
            <Notice tone="success" title="Imported" testId="import-done">
              {run.total} changes were made, and the catalog now matches the file.
            </Notice>
          )}
          {run?.finished && !run.failed && !nothingToDo && plan.steps.length > 0 && (
            <Notice
              tone="warning"
              title="Imported, but the check still finds changes"
              testId="import-left"
            >
              Someone may have changed the catalog meanwhile. Review the check above before
              importing again.
            </Notice>
          )}
          {plan.steps.length > 0 && (
            <div className="d-inline">
              <Button
                variant="primary"
                disabled={plan.blocked || running || !online}
                onClick={() => {
                  setChanged(false);
                  setConfirmed(plan.steps.map((st) => st.stepId));
                  setConfirming(true);
                }}
                data-testid="import-apply"
              >
                Import {plan.steps.length} changes
              </Button>
              {!online && <span className="d-muted">Not while the connection is down.</span>}
            </div>
          )}
        </Section>
      )}

      <ConfirmSheet
        open={confirming}
        title={`Import ${plan?.steps.length ?? 0} changes into ${businessName}?`}
        confirmLabel="Import"
        tone="primary"
        onConfirm={() => void apply()}
        onClose={() => setConfirming(false)}
        testId="import-confirm"
      >
        <p>
          These are ordinary catalog changes, each recorded in the audit log under your name. No
          sale, payment or money record is created, and nothing in the catalog is archived.
        </p>
      </ConfirmSheet>
    </div>
  );
}
